import * as Dialog from "@radix-ui/react-dialog";
import { useAtom } from "jotai";
import { BookOpen, CornerDownLeft, Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { MethodChip } from "@components/MethodChip.tsx";
import { cn } from "@lib/cn.ts";
import type { SearchDoc } from "@lib/search.ts";
import { searchOpenAtom } from "@lib/state.ts";

/**
 * The search engine and its index are pulled in only when the palette is first
 * opened. Most readers arrive from a link and never search, and this keeps the
 * full-text engine off the critical path for them.
 */
const loadSearch = () => import("@lib/search.ts");

interface Props {
  version: string;
}

export function SearchPalette({ version }: Props) {
  const [open, setOpen] = useAtom(searchOpenAtom);
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<SearchDoc[]>([]);
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement>(null);

  /* Cmd/Ctrl+K anywhere, plus "/" when not already typing in a field. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      const isShortcut =
        (event.key === "k" && (event.metaKey || event.ctrlKey)) ||
        (event.key === "/" &&
          !(event.target instanceof HTMLInputElement) &&
          !(event.target instanceof HTMLTextAreaElement));

      if (isShortcut) {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);

  useEffect(() => {
    if (!open) {
      setTerm("");
      setActive(0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    void loadSearch().then(async ({ searchDocs, defaultSuggestions }) => {
      const hits = term.trim()
        ? await searchDocs(version, term)
        : await defaultSuggestions(version);

      if (!cancelled) {
        setResults(hits);
        setActive(0);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [term, version, open]);

  const go = (doc: SearchDoc | undefined): void => {
    if (!doc) return;
    setOpen(false);
    void navigate(doc.href);
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (event.key === "Enter") {
      event.preventDefault();
      go(results[active]);
    }
  };

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [active]);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
        <Dialog.Content
          aria-label="Search the documentation"
          className={cn(
            "fixed left-1/2 top-[12vh] z-50 w-[min(38rem,calc(100vw-2rem))] -translate-x-1/2",
            "overflow-hidden rounded-default border border-line bg-bg shadow-2xl",
          )}
        >
          <Dialog.Title className="sr-only">Search</Dialog.Title>

          <div className="flex items-center gap-2 border-b border-line px-3">
            <Search className="size-4 shrink-0 text-subtle" aria-hidden />
            <input
              autoFocus
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search endpoints and guides"
              aria-label="Search query"
              className="w-full bg-transparent py-3 text-[14px] text-ink outline-none placeholder:text-subtle"
            />
            <kbd className="hidden shrink-0 rounded-sm2 border border-line px-1.5 py-0.5 text-[10.5px] text-subtle sm:block">
              esc
            </kbd>
          </div>

          <div
            ref={listRef}
            className="gt-scroll max-h-[55vh] overflow-y-auto p-1"
          >
            {results.length === 0 ? (
              <p className="px-3 py-6 text-center text-[13px] text-muted">
                {term.trim()
                  ? `Nothing matches "${term}".`
                  : "Start typing to search."}
              </p>
            ) : (
              results.map((doc, index) => (
                <button
                  key={doc.id}
                  type="button"
                  data-index={index}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => go(doc)}
                  className={cn(
                    "flex w-full items-start gap-2.5 rounded-sm2 px-2.5 py-2 text-left transition",
                    index === active ? "bg-surface-2" : "hover:bg-surface",
                  )}
                >
                  <span className="mt-0.5 shrink-0">
                    {doc.kind === "operation" ? (
                      <MethodChip method={doc.subtitle.split(" ")[0]} />
                    ) : (
                      <BookOpen className="size-3.5 text-subtle" aria-hidden />
                    )}
                  </span>

                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13.5px] font-medium text-ink">
                      {doc.title}
                    </span>
                    <span className="block truncate font-mono text-[11.5px] text-subtle">
                      {doc.kind === "operation"
                        ? doc.subtitle.split(" ").slice(1).join(" ")
                        : doc.subtitle}
                    </span>
                  </span>

                  {index === active ? (
                    <CornerDownLeft
                      className="mt-1 size-3.5 shrink-0 text-subtle"
                      aria-hidden
                    />
                  ) : null}
                </button>
              ))
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
